import { useMutation, useMutationState, useQueryClient } from '@tanstack/react-query'
import { useCallback, useRef } from 'react'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import { formatCents } from '../../api/parseMoney'
import type { components } from '../../api/generated/schema'
import { mintUuid } from '../../platform/mintUuid'
import type { LocalDate } from './recurringRules'
import { emitCreated, emitEdited } from './recurringAnalytics'
import { billKind, createFromTransaction, patchRecurring, RecurringWriteError } from './recurringApi'
import type { Txn } from '../transactions/transactionWire'
import { toBill, type Bill, type Frequency } from './recurringWire'

/**
 * The transaction a bill is being made from: what the form prefills and shows.
 * `amountCents` keeps the transaction's own sign (an expense is negative); the
 * form reads its magnitude. The server derives amount, account and merchant
 * itself, so none of it is sent (Ruling 20).
 */
export type SourceTxn = Pick<Txn, 'id' | 'merchant' | 'amountCents' | 'currency' | 'accountId' | 'date' | 'categoryId'>

/** Where the form was opened from. */
export type FormMode =
  | { kind: 'create' }
  | { kind: 'edit'; bill: Bill }
  | { kind: 'fromTransaction'; txn: SourceTxn }

/**
 * What the form holds once the amount text has been read. `frequency` admits
 * `unknown` for one case only: an edit of a bill the server could not classify,
 * left as it was — an unchanged key is never sent, so `unknown` never reaches
 * the wire. A create always starts at Monthly.
 */
export type FormValues = {
  name: string
  amountCents: number | null
  frequency: Frequency
  nextDue: LocalDate
  accountId: string | null
  /** A catalogue slug picked in this form, else the bill's stored icon (edit) or null. */
  icon: string | null
  /** Create only, and sent only with `applyToPast` (Ruling 19). */
  payingSince: LocalDate | null
  applyToPast: boolean
}

/** The one currency fallback for a bill with no account (Ruling 44): `Money`'s documented default. */
export const FALLBACK_CURRENCY = 'CAD'

export const FORM_COPY = {
  nameRequired: 'Give this bill a name.',
  amountRequired: 'Enter an amount greater than zero.',
  /** iOS settings' wording for text `parseMoney` cannot read. */
  amountUnreadable: "That isn't an amount we can read.",
  /** NEW COPY. */
  nextDueRequired: 'Choose the date it is next due.',
  /** NEW COPY: the date input's own `max` is not enforced on typed input. */
  payingSinceAfterDue: 'Paying since must be on or before the next due date.',
  /** NEW COPY. */
  payingSinceRequired: 'Choose the date to match past payments from.',
} as const

/** iOS `saveFailureMessage`, verbatim. */
export const SAVE_COPY = {
  failed: "Couldn't save. Try again — you won't get a duplicate.",
  invalid: 'Check the name, amount and date, then try again.',
  /** S13, the FORM's wording (iOS Q28): the form keeps the user's input, so it cannot say "Showing the latest". */
  conflict: 'This bill changed on another device. Close and reopen it to see the latest.',
  removed: 'This bill was removed on another device.',
} as const

/** The same two outcomes, worded for a transaction: it, not a bill, is what conflicted or went. */
export const FROM_TXN_COPY = {
  conflict: 'That transaction is already part of a recurring payment.',
  removed: 'That transaction is no longer available.',
} as const

/**
 * Every rule a value must pass before anything is sent. Field order, so the
 * first key is the first field to focus. The amount's "unreadable" case is the
 * form's: by the time a `FormValues` exists the text is already cents or null.
 */
export function validate(v: FormValues): Partial<Record<keyof FormValues, string>> {
  const errors: Partial<Record<keyof FormValues, string>> = {}
  if (v.name.trim() === '') errors.name = FORM_COPY.nameRequired
  if (v.amountCents === null || v.amountCents <= 0) errors.amountCents = FORM_COPY.amountRequired
  if (v.nextDue === '') errors.nextDue = FORM_COPY.nextDueRequired
  if (sendsHistory(v)) {
    if (v.payingSince === null || v.payingSince === '') errors.payingSince = FORM_COPY.payingSinceRequired
    // Date-only strings compare as calendar dates lexically; never `new Date(str)`.
    else if (v.nextDue !== '' && v.payingSince > v.nextDue) errors.payingSince = FORM_COPY.payingSinceAfterDue
  }
  return errors
}

/** Apply to past only means something with an account: the server backfills on `account_id` + `first_date`. */
const sendsHistory = (v: FormValues): boolean => v.applyToPast && v.accountId !== null

/**
 * The values an edit starts from: what "changed" is measured against.
 *
 * **The amount is the figure the edit will write.** A fixed bill's amount is
 * its expected amount (`updateBody` moves `expected_amount` with it); a
 * variable bill's is its average, because `updateBody` writes only
 * `average_amount` for one. Seeding a variable bill from `expectedCents` made
 * an edit compare one field and write another — a silent no-op whenever the
 * bill carried an `expected_amount`.
 *
 * **A bill with no next due date opens with the field empty**, and `validate`
 * then requires one before an edit can save: a stream with no
 * `predicted_next_date` has no anchor and never reaches the calendar (S15).
 */
export function valuesOf(bill: Bill): FormValues {
  return {
    name: bill.name,
    amountCents: bill.amountType === 'fixed' ? bill.expectedCents : bill.averageCents,
    frequency: bill.frequency,
    nextDue: bill.nextDue ?? '',
    accountId: bill.accountId,
    icon: bill.icon,
    payingSince: null,
    applyToPast: false,
  }
}

type CreateBody = components['schemas']['RecurringCreate']
type UpdateBody = components['schemas']['RecurringUpdate']
type FromTxnBody = components['schemas']['CreateFromTransactionRequest']

/**
 * `POST /recurring`'s body.
 *
 * - **`predicted_next_date` always** (S15): a stream with no anchor never
 *   appears in the calendar, whatever else is set.
 * - **`first_date` only with Apply to past** (Ruling 19). The server backfills
 *   when `account_id` and `first_date` are both present, so `first_date` is
 *   the switch. The client never snaps it to the bill's grid (S18).
 * - **`account_id` whenever one is chosen.** It is the bill's "Paid from", a
 *   property of the bill; without `first_date` beside it nothing is imported.
 * - **`iso_currency_code` only with no account.** With one, the server takes
 *   the currency from the real account; without one it would default to USD,
 *   so the caller's fallback is sent (Ruling 44).
 */
export function createBody(v: FormValues, fallbackCurrency: string): CreateBody {
  // `iso_currency_code` is typed required only because the server has a
  // default for it; omitting it is the documented way to take that path.
  const body = {
    merchant_name: v.name.trim(),
    average_amount: formatCents(v.amountCents ?? 0),
    frequency: v.frequency,
    direction: 'outflow',
    predicted_next_date: v.nextDue,
    reminder_days: 3,
    is_trial: false,
    amount_type: 'variable',
  } as CreateBody
  if (v.accountId !== null) body.account_id = v.accountId
  else body.iso_currency_code = fallbackCurrency
  if (v.icon !== null) body.icon = v.icon
  if (sendsHistory(v) && v.payingSince !== null) body.first_date = v.payingSince
  return body
}

/**
 * Ruling 46: the endpoint stores the transaction's raw bank string as the
 * bill's `description` and puts a `custom_name` into `merchant_name`, so the
 * bill would display something other than what the form showed. When the name
 * differs, one follow-up PATCH writes the form's name as `description`. If that
 * fails the bill still exists, so the save is still a success: the user can
 * rename it from the bill.
 */
async function showTheNameTheFormShowed(created: Bill, name: string): Promise<Bill> {
  if (created.name === name) return created
  try {
    return await patchRecurring(created.id, { description: name, version: created.version })
  } catch {
    return created
  }
}

/**
 * `PATCH /recurring/{id}`'s body: what the user chose and
 * nothing the transaction already says (Ruling 20). The start date is the form's
 * "Paying since"; `custom_name` only when the name was changed from the
 * merchant, so an untouched name stays the server's own.
 */
export function fromTransactionBody(txn: SourceTxn, v: FormValues): FromTxnBody {
  const body = { frequency: v.frequency, start_date: v.payingSince ?? txn.date } as FromTxnBody
  const name = v.name.trim()
  if (name !== txn.merchant) body.custom_name = name
  if (v.icon !== null) body.icon = v.icon
  return body
}

/**
 * `PATCH /recurring/{id}`'s body: the keys the user changed, plus `version`.
 * Null when nothing changed, so a Save that would only bump `version` sends
 * nothing.
 *
 * - The name is written as `description`: the bill shows
 *   `description ?? merchant_name`, and `merchant_name` is what the matcher
 *   reads, so renaming it would quietly stop past and future charges matching.
 * - A FIXED bill's expected amount IS the amount this field edits (iOS
 *   `draft()`); a variable bill's expected amount is a figure this form does
 *   not offer, so it is left alone.
 * - Clearing "Paid from" is an explicit `account_id: null`.
 */
export function updateBody(bill: Bill, v: FormValues): UpdateBody | null {
  const was = valuesOf(bill)
  const body: UpdateBody = {}
  const name = v.name.trim()
  if (name !== was.name) body.description = name
  if (v.amountCents !== null && v.amountCents !== was.amountCents) {
    body.average_amount = formatCents(v.amountCents)
    if (bill.amountType === 'fixed') body.expected_amount = formatCents(v.amountCents)
  }
  if (v.frequency !== was.frequency && v.frequency !== 'unknown') body.frequency = v.frequency
  if (v.nextDue !== was.nextDue && v.nextDue !== '') body.predicted_next_date = v.nextDue
  if (v.accountId !== was.accountId) body.account_id = v.accountId
  if (v.icon !== was.icon && v.icon !== null) body.icon = v.icon
  if (Object.keys(body).length === 0) return null
  return { ...body, version: bill.version }
}

/** Why a save did not happen. `message` is the sentence the form shows as it stands. */
export class SaveError extends Error {
  readonly kind: 'conflict' | 'invalid' | 'removed' | 'failed'
  constructor(kind: SaveError['kind'], cause?: unknown, message: string = SAVE_COPY[kind]) {
    super(message, { cause })
    this.name = 'SaveError'
    this.kind = kind
  }
}

export type Saved = {
  id: string
  /** Past payments the create linked (Apply to past only). */
  linked?: number
  /** The server's next due date, for the "Added · due Aug 1" toast. */
  nextDue: LocalDate | null
  /** The bill as the server now has it. */
  bill: Bill
}

/** A create's failure: there is no `version` to conflict on and no bill to be removed. */
function createFailure(status: number, cause: unknown): SaveError {
  return new SaveError(status === 422 ? 'invalid' : 'failed', cause)
}

async function send(mode: FormMode, v: FormValues, fallbackCurrency: string, idempotencyKey: string): Promise<Saved | null> {
  if (mode.kind === 'fromTransaction') {
    try {
      const created = await createFromTransaction(mode.txn.id, fromTransactionBody(mode.txn, v))
      const bill = await showTheNameTheFormShowed(created, v.name.trim())
      return { id: bill.id, nextDue: bill.nextDue, bill }
    } catch (e) {
      if (!(e instanceof RecurringWriteError)) throw new SaveError('failed', e)
      const sentence = e.kind === 'conflict' ? FROM_TXN_COPY.conflict : e.kind === 'removed' ? FROM_TXN_COPY.removed : undefined
      throw new SaveError(billKind(e), e.cause, sentence)
    }
  }
  if (mode.kind === 'edit') {
    const body = updateBody(mode.bill, v)
    if (body === null) return null
    try {
      const bill = await patchRecurring(mode.bill.id, body)
      return { id: bill.id, nextDue: bill.nextDue, bill }
    } catch (e) {
      throw e instanceof RecurringWriteError ? new SaveError(billKind(e), e.cause) : new SaveError('failed', e)
    }
  }
  let result
  try {
    result = await api.POST('/api/v1/recurring', {
      params: { header: { 'Idempotency-Key': idempotencyKey } },
      body: createBody(v, fallbackCurrency),
    })
  } catch (cause) {
    throw new SaveError('failed', cause)
  }
  const { data, error, response } = result
  if (error !== undefined || data === undefined) throw createFailure(response.status, error)
  const bill = toBill(data)
  return {
    id: bill.id,
    nextDue: bill.nextDue,
    bill,
    ...(sendsHistory(v) ? { linked: data.payment_count } : {}),
  }
}

/**
 * Create (`POST /recurring`) or edit (`PATCH /recurring/{id}`) one bill.
 *
 * **A mutation in the client's cache** (`keys.recurring.save`), the shape
 * `useMarkPaid`, `useLifecycle` and `usePeriodLinks` share: `saving` is read
 * from the mutation cache, so a form that remounts mid-save still says so.
 *
 * **One request per form at a time.** A second `save` while the first is out
 * returns the SAME promise — a double click sends one POST and both callers
 * hear its answer. The fold is a ref, not state, so a second click in the same
 * tick still sees it. (The form itself lets only the first caller act on the
 * answer.)
 *
 * **The `Idempotency-Key` is minted once per mount** (`mintUuid`, which
 * survives a missing `crypto.randomUUID`) and reused by every retry, so a
 * retry after a lost response returns the bill already created instead of a
 * second one (UC-13) — which is what "you won't get a duplicate" promises.
 *
 * **An edit's 409 is never retried.** The form holds the bill it opened with,
 * so a second Save 409s again rather than overwriting the other device with a
 * `version` it never showed the user (Review Focus 4).
 *
 * Every settle — success or failure — invalidates the `['recurring']` root.
 * Nothing toasts here: the form shows each outcome where the user clicked.
 *
 * Resolves null when an edit changed nothing (no request was sent).
 */
export function useSaveRecurring(
  mode: FormMode,
  fallbackCurrency: string,
): { save: (v: FormValues) => Promise<Saved | null>; saving: boolean } {
  const client = useQueryClient()
  const key = useRef<string | null>(null)
  key.current ??= mintUuid()
  const inFlight = useRef<Promise<Saved | null> | null>(null)
  const mutationKey =
    mode.kind === 'edit'
      ? keys.recurring.save('edit', mode.bill.id)
      : mode.kind === 'fromTransaction'
        ? keys.recurring.save('fromTransaction', mode.txn.id)
        : keys.recurring.save('create', null)
  const saving = useMutationState({ filters: { mutationKey, exact: true, status: 'pending' } }).length > 0

  const { mutateAsync } = useMutation<Saved | null, Error, FormValues>({
    mutationKey,
    mutationFn: (v) => send(mode, v, fallbackCurrency, key.current as string),
    // Null is an edit that changed nothing: no request, no event.
    onSuccess: (saved) => {
      if (saved === null) return
      if (mode.kind === 'edit') emitEdited()
      else emitCreated(mode.kind === 'fromTransaction' ? 'from_transaction' : 'manual')
    },
    // Not awaited: the caller settles on the server's answer.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.recurring.all() })
      // A new bill may have taken a transaction: its detail says "Part of …" now.
      if (mode.kind !== 'edit') void client.invalidateQueries({ queryKey: keys.transactions.all() })
    },
  })

  const save = useCallback(
    (v: FormValues): Promise<Saved | null> => {
      if (inFlight.current !== null) return inFlight.current
      const run = mutateAsync(v).finally(() => {
        inFlight.current = null
      })
      inFlight.current = run
      return run
    },
    [mutateAsync],
  )

  return { save, saving }
}
